/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useEffect, useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import EmailLink from '../../../../client/js/components/links/EmailLink'
import PhoneLink from '../../../../client/js/components/links/PhoneLink'
import { isBuildingEstate, isPhysicalEstate } from '../../lib/estate.mjs'
import { fetchEstate } from '../../lib/rest/estateAPIClient'
import FormattedSurface from '../FormattedSurface'
import EstateTypeLabel from '../labels/EstateTypeLabel'
import PriceLabel from '../labels/PriceLabel'
import TransactionLabel from '../labels/TransactionLabel'
import LabelledValue from '../LabelledValue'
import Ref from '../Ref'
import { useQuery } from '@tanstack/react-query'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { Box, Container, Group, Image, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import AppLogo from '../../../../client/js/components/AppLogo'
import Amount from '../transaction/Amount'
import { parseNumber } from '@jalik/form-parser'

function print () {
  // eslint-disable-next-line no-console
  console.log('PRINT')

  if (window.location.hostname !== 'localhost') {
    window.print()
  }
}

const MAX_PHOTOS = 3

function EstatePrintPage () {
  const intl = useIntl()
  const params = useParams<{ id: string }>()
  const estateId = parseNumber(params.id)

  // Estate loading.
  const estateQuery = useQuery({
    enabled: true,
    queryKey: ['estate', estateId],
    queryFn: () => fetchEstate(estateId)
  })
  const { data: estate } = estateQuery
  const isReady = estateQuery.isFetched

  const divRef = useRef<HTMLDivElement>(null)

  // Prepare photos.
  const photos = useMemo(() => {
    const list = []

    if (estate != null && estate.Photos instanceof Array) {
      const maxPhotos = Math.min(MAX_PHOTOS, estate.Photos.length)
      for (let i = 0; i < maxPhotos; i += 1) {
        list.push(estate.Photos[i])
      }
    }
    return list
  }, [estate])

  useEffect(() => {
    const listeners: Array<[HTMLImageElement, () => void]> = []

    if (isReady && typeof window !== 'undefined') {
      // wait until all images are loaded
      const images = divRef.current.getElementsByTagName('img')

      let loadedImages = 0
      const totalImages = images.length

      const onLoad = () => {
        loadedImages += 1

        if (loadedImages === totalImages) {
          print()
        }
      }

      if (totalImages > 0) {
        for (let i = 0; i < images.length; i += 1) {
          listeners.push([images[i], onLoad])
          images[i].addEventListener('load', onLoad)
        }
      } else {
        print()
      }
    }
    return () => {
      listeners.forEach(([img, listener]) => {
        img.removeEventListener('load', listener)
      })
    }
  }, [isReady])

  return (
    <Container py="xl" ref={divRef}>
      <LoadingHelper
        query={estateQuery}
        render={() => (
          <Stack gap="xl">
            <Helmet>
              <title>
                {intl.formatMessage({
                  id: 'printing_%',
                  defaultMessage: 'Impression de {subject}'
                }, {
                  subject: estate.reference
                })}
              </title>
            </Helmet>
            <header>
              <Group align="start">
                <AppLogo w={90} h={90} />
                <Text size="sm">
                  <Text fw="bold">TAHITI WELCOME HOME</Text>
                  <div><PhoneLink phone="+689 40 851 448" /></div>
                  <div><PhoneLink phone="+689 87 713 513" /></div>
                  <div><EmailLink email="contact@welcomehome.immo" /></div>
                  <div>
                    <AnchorLink to="https://www.welcomehome.immo/">https://www.welcomehome.immo</AnchorLink>
                  </div>
                  <div>Punaauia Pk 14 c/mer en face de Mania & Co | Servitude
                    Sage 1
                  </div>
                </Text>
                <Stack ms="auto" gap={0} align="end">
                  <Ref
                    fw="bold"
                    size="xl"
                    value={estate.reference}
                  />
                  <Text size="xl">
                    {estate.price != null && estate.isPricePublished
                      ? <Amount value={estate.price} inherit withCurrency />
                      : intl.formatMessage({
                        id: 'contact_us',
                        defaultMessage: 'Contactez-nous'
                      })
                    }
                  </Text>
                </Stack>
              </Group>
            </header>

            <div style={{ lineHeight: 1.2 }}>
              <Title>{estate.title}</Title>
              <Text>
                {estate.description}
              </Text>

              {!estate.published
                ? <Box bg="red">
                  <FormattedMessage
                    id="estate_not_published_warning"
                    defaultMessage="Ce bien n'est pas publié sur le site."
                  />
                </Box>
                : null}
            </div>

            <SimpleGrid cols={{ base: 2 }}>
              <Stack>
                <Title order={2}>
                  <FormattedMessage
                    id="general"
                    defaultMessage="Général"
                  />
                </Title>
                <div>
                  <LabelledValue
                    label={intl.formatMessage({
                      id: 'reference',
                      defaultMessage: 'Référence'
                    })}
                    value={(
                      <Ref value={estate.reference} />
                    )}
                  />
                  <LabelledValue
                    label={intl.formatMessage({
                      id: 'transaction',
                      defaultMessage: 'Transaction'
                    })}
                    value={<TransactionLabel value={estate.transaction} />}
                  />
                  <LabelledValue
                    label={intl.formatMessage({
                      id: 'type',
                      defaultMessage: 'Type'
                    })}
                    value={<EstateTypeLabel value={estate.type} />}
                  />
                  {estate.City
                    ? <LabelledValue
                      label={intl.formatMessage({
                        id: 'city',
                        defaultMessage: 'Ville'
                      })}
                      value={estate.City.name}
                    />
                    : null}
                  {estate.residence
                    ? <LabelledValue
                      label={intl.formatMessage({
                        id: 'residence',
                        defaultMessage: 'Résidence'
                      })}
                      value={estate.residence}
                    />
                    : null}
                </div>

                <Title order={2}>
                  <FormattedMessage
                    id="description"
                    defaultMessage="Description"
                  />
                </Title>
                <div>
                  {isPhysicalEstate(estate.type) && estate.landArea != null
                    ? <LabelledValue
                      label={intl.formatMessage({
                        id: 'land_area',
                        defaultMessage: 'Superficie du terrain'
                      })}
                      value={<FormattedSurface value={estate.landArea} />}
                    />
                    : null}

                  {isBuildingEstate(estate.type)
                    ? <>
                      {estate.surface != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'surface',
                            defaultMessage: 'Superficie'
                          })}
                          value={<FormattedSurface value={estate.surface} />}
                        />
                        : null}

                      {estate.terraceArea != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'terrace_area',
                            defaultMessage: 'Superficie de la terrasse'
                          })}
                          value={
                            <FormattedSurface value={estate.terraceArea} />}
                        />
                        : null}

                      {estate.furnished != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'furnished',
                            defaultMessage: 'Meublé'
                          })}
                          value={estate.furnished}
                        />
                        : null}

                      {estate.equipped != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'equipped',
                            defaultMessage: 'Équipé'
                          })}
                          value={estate.equipped}
                        />
                        : null}

                      {estate.floors != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'floors',
                            defaultMessage: 'Étages'
                          })}
                          value={estate.floors}
                        />
                        : null}

                      {estate.rooms != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'rooms',
                            defaultMessage: 'Pièces'
                          })}
                          value={estate.rooms}
                        />
                        : null}

                      {estate.bedrooms != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'bedrooms',
                            defaultMessage: 'Chambres'
                          })}
                          value={estate.bedrooms}
                        />
                        : null}

                      {estate.bathrooms != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'bathrooms',
                            defaultMessage: 'Salles de bains'
                          })}
                          value={estate.bathrooms}
                        />
                        : null}

                      {estate.toilets != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'toilets',
                            defaultMessage: 'WC'
                          })}
                          value={estate.toilets}
                        />
                        : null}

                      {estate.livingRooms != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'livingRooms',
                            defaultMessage: 'Salons'
                          })}
                          value={estate.livingRooms}
                        />
                        : null}

                      {estate.kitchens != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'kitchens',
                            defaultMessage: 'Cuisines'
                          })}
                          value={estate.kitchens}
                        />
                        : null}

                      {estate.parking != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'parking_places',
                            defaultMessage: 'Places de parking'
                          })}
                          value={estate.parking}
                        />
                        : null}

                      {estate.constructionYear != null
                        ? <LabelledValue
                          label={intl.formatMessage({
                            id: 'constructionYear',
                            defaultMessage: 'Année de construction'
                          })}
                          value={estate.constructionYear}
                        />
                        : null}
                    </>
                    : null}
                </div>

                <Title order={2}>
                  <FormattedMessage
                    id="charges"
                    defaultMessage="Charges"
                  />
                </Title>
                <div>
                  {estate.price != null && estate.isPricePublished
                    ? <LabelledValue
                      label={PriceLabel({ value: estate.transaction })}
                      value={<Amount value={estate.price} inherit withCurrency />}
                    />
                    : null}

                  {estate.securityDeposit != null
                    ? <LabelledValue
                      label={intl.formatMessage({
                        id: 'security_deposit',
                        defaultMessage: 'Dépôt de garantie'
                      })}
                      value={<Amount value={estate.securityDeposit} inherit withCurrency />}
                    />
                    : null}

                  {estate.agencyFees != null && estate.isAgencyFeesWithTax
                    ? <LabelledValue
                      label={intl.formatMessage({
                        id: 'agency_fees_including_tax',
                        defaultMessage: 'Honoraires de location TTC'
                      })}
                      value={<Amount value={estate.agencyFees} inherit withCurrency />}
                    />
                    : null}

                  {estate.agencyFees != null && !estate.isAgencyFeesWithTax
                    ? <LabelledValue
                      label={intl.formatMessage({
                        id: 'agency_fees_excluding_tax',
                        defaultMessage: 'Honoraires de location HT'
                      })}
                      value={<Amount value={estate.agencyFees} inherit withCurrency />}
                    />
                    : null}

                  {estate.Charges?.map((charge) => (
                    <LabelledValue
                      key={charge.label}
                      label={charge.label}
                      value={<>
                        <Amount value={charge.amount} inherit withCurrency />
                        /{charge.frequency}
                      </>}
                    />
                  ))}
                </div>
              </Stack>
              <Stack>
                {photos.map((photo) => (
                  <Image
                    key={photo.url}
                    alt={photo.name}
                    src={photo.url}
                    radius="md"
                  />
                ))}
              </Stack>
            </SimpleGrid>
          </Stack>
        )}
      />
    </Container>
  )
}

export default EstatePrintPage
